/**
 * Market/contract closure helpers.
 *
 * Trading closes per venue: each contract carries its own `last_trade_time`
 * (epoch seconds), so in a multi-venue strike one exchange can be closed while
 * another still trades. A strike/group is only "closed" once every venue in it
 * is closed. Values that are missing, zero, or in the future are treated as open.
 */
import type { PredictionContractListContract } from '@/types/prediction.types';
import type { ForecastTraderStrike } from '@/types/forecasttrader.types';

/** True when the given last trade time (epoch seconds) is a real time in the past. */
export function isMarketClosed(lastTradeTime?: number | null): boolean {
  if (lastTradeTime == null || lastTradeTime <= 0) return false;
  return lastTradeTime * 1000 <= Date.now();
}

/** True when a single venue-contract is closed for trading. */
export function isContractClosed(contract: PredictionContractListContract): boolean {
  return isMarketClosed(contract.last_trade_time);
}

/** True when every venue in a strike group (list of venue contracts) is closed. */
export function isGroupClosed(contracts: PredictionContractListContract[]): boolean {
  return contracts.length > 0 && contracts.every((c) => isContractClosed(c));
}

/** True when a markets-page strike is closed for trading. */
export function isStrikeClosed(strike: ForecastTraderStrike): boolean {
  return isMarketClosed(strike.last_trade_time);
}
